import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import axiosApiInstance from '../../../../api/axiosClient';
import axios from 'axios';
import {PageContextProvider} from '../DDEXMainPage';
import {Button, message, Spin, Steps, Typography, UploadFile} from 'antd';
import {useAtom} from 'jotai';
import '../ReleaseEntryStepForm.css';
import {isReleaseInfoLoadingAtom, warningsAtom} from '../ddex_atoms';
import {confirmationModalAtom} from './types';
import {auth} from "../../../../config/firebase";
import {API} from "../../../../common/constants";
import {RcFile} from "antd/lib/upload";
import {deleteReleaseFile, fetchReleaseFiles, stripConsecutiveSpacesInFilename} from '../fileUtils';

// Import components
import ExcelUploadStep from './ExcelUploadStep';
import FileUploadStep from './FileUploadStep';
import VerificationStep from './VerificationStep';
import {BatchCreationModal, ConfirmationModal, ReuploadExcelModal} from './BatchModals';

// Import types and atoms
import {batchAtom, batchItemsAtom, BatchUploadStep, currentStepAtom, previewDataAtom, uploadedFilesAtom} from './types';

const {Title} = Typography;

// Define the BatchReleasePage component
const BatchReleasePage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [reuploadModalVisible, setReuploadModalVisible] = useState(false);
    const [batchName, setBatchName] = useState('');
    const [batchDescription, setBatchDescription] = useState('');
    const [excelFile, setExcelFile] = useState<RcFile | null>(null);
    const [uploadingExcel, setUploadingExcel] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [processingBatch, setProcessingBatch] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<UploadFile<any>[]>([]);
    const [fetchingExistingFiles, setFetchingExistingFiles] = useState(false);

    // Atoms
    const [batch, setBatch] = useAtom(batchAtom);
    const [batchItems, setBatchItems] = useAtom(batchItemsAtom);
    const [currentStep, setCurrentStep] = useAtom(currentStepAtom);
    const [uploadedFiles, setUploadedFiles] = useAtom(uploadedFilesAtom);
    const [previewData, setPreviewData] = useAtom(previewDataAtom);
    const [warnings, setWarnings] = useAtom(warningsAtom);
    const [confirmationModal, setConfirmationModal] = useAtom(confirmationModalAtom);
    const [isReleaseInfoLoading, setIsReleaseInfoLoading] = useAtom(isReleaseInfoLoadingAtom);

    // For backward compatibility with PageContextProvider
    const releasesActionRef = React.useRef<any>(null);
    const listRef = React.useRef<any>(null);
    const [artists, setArtists] = React.useState<any>({});

    // If no ID is provided in the URL, show the modal to enter batch name
    useEffect(() => {
        if (!id) {
            setModalVisible(true);
            setLoading(false);
        } else {
            // Load batch data
            fetchBatchData(id);
        }
    }, [id]);

    useEffect(() => {
        // If navigating to file upload or verify step, fetch existing files
        if (currentStep === 'upload-files' || currentStep === 'verify') {
            fetchAllReleaseFiles(batchItems);

            // Also fetch preview data when navigating to verify step
            if (currentStep === 'verify') {
                fetchBatchPreviews();
            }
        }
    }, [currentStep]);


    // Function to fetch batch data
    const fetchBatchData = async (batchId: string) => {
        try {
            setLoading(true);
            const response = await axiosApiInstance.get(`/api/products/batch_releases/${batchId}`);
            if (response.status === 200) {
                setBatch(response.data);
                setBatchItems(response.data.batch_items || []);

                if (currentStep === 'upload-files') {
                    await fetchAllReleaseFiles(response.data.batch_items || []);

                } else if (currentStep === 'verify') {
                    await fetchAllReleaseFiles(response.data.batch_items || []);
                    // Fetch preview data for verification step
                    await fetchBatchPreviews();
                }

                setLoading(false);
            } else {
                throw new Error('Failed to fetch batch data');
            }
        } catch (err) {
            console.error('Error fetching batch data:', err);
            setError('Failed to fetch batch data');
            setLoading(false);
        }
    };

    // Function to fetch already uploaded files for all releases in the batch
    const fetchAllReleaseFiles = async (items: API.MusicDistribution.ReleaseBatchItem[]) => {
        if (!items || items.length === 0) return;

        setFetchingExistingFiles(true);

        try {
            // Create a new object to store all uploaded files by release ID
            const allUploadedFiles: Record<string, any[]> = {};

            // Fetch files for each release in the batch
            for (const item of items) {
                if (!item.release_id) continue;

                console.log(`Fetching files for release: ${item.release_id}`);

                // Use the fetchReleaseFiles function to get files for this release
                const files = await fetchReleaseFiles(
                    item.release_id,
                    (fetchedFiles) => {
                        // Store the fetched files in our object
                        allUploadedFiles[item.release_id] = fetchedFiles;
                    }
                );

                console.log(`Found ${files.length} files for release ${item.release_id}`);
            }

            // Update the uploadedFiles state with all fetched files
            setUploadedFiles(allUploadedFiles);
            console.log('All release files fetched:', allUploadedFiles);

        } catch (error) {
            console.error('Error fetching release files:', error);
            message.error('Failed to fetch existing files');
        } finally {
            setFetchingExistingFiles(false);
        }
    };

    // Function to handle the modal submission
    const handleModalSubmit = async () => {
        if (!batchName.trim()) {
            message.error('Please enter a batch name');
            return;
        }

        setLoading(true);

        // Create a batch entry with the name and description
        const values = {
            name: batchName,
            description: batchDescription,
            status: 'pending'
        };

        try {
            const response = await axiosApiInstance.post('/api/products/batch_releases/', values);
            if (response.status === 200 && response.data?.id) {
                // Navigate to the batch page with the new ID
                navigate(`/music/batch/${response.data.id}`, {replace: true});
                setModalVisible(false);
                setBatch(response.data);
            } else {
                throw new Error('Failed to create batch entry');
            }
        } catch (err) {
            console.error('Error creating batch entry:', err);
            setError('Failed to create batch entry');
            setLoading(false);
        }
    };

    // Function to handle Excel file upload
    const handleExcelUpload = async (isReupload = false) => {
        if (!excelFile) {
            message.error('Please select an Excel file');
            return;
        }

        setUploadingExcel(true);
        // Clear any previous errors
        setUploadError(null);

        try {
            const formData = new FormData();
            formData.append('file', excelFile);
            const token = await auth.currentUser?.getIdToken()
            const endpoint = API + `/api/products/batch_releases/${id}/import`;

            const response = await axios.post(endpoint, formData, {
                headers: {
                    "Accept": "*/*",
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                },
            });
            if (response.status === 200) {
                message.success('Excel file uploaded successfully');
                if (isReupload) {
                    setReuploadModalVisible(false);
                }
                await fetchBatchData(id as string);
                // Fetch preview data to show validation results in ExcelUploadStep
                await fetchBatchPreviews();
                setCurrentStep('upload-excel');
            } else {
                throw new Error('Failed to upload Excel file');
            }
        } catch (err: any) {
            console.error('Error uploading Excel file:', err);

            // Extract detailed error message if available
            let errorMessage = 'Failed to upload Excel file';

            if (err.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                if (err.response.data && err.response.data.error) {
                    errorMessage = err.response.data.error;
                } else if (err.response.data && err.response.data.message) {
                    errorMessage = err.response.data.message;
                } else if (typeof err.response.data === 'string') {
                    errorMessage = err.response.data;
                } else {
                    errorMessage = `Server error: ${err.response.status}`;
                }
            } else if (err.request) {
                // The request was made but no response was received
                errorMessage = 'No response from server. Please check your network connection.';
            } else if (err.message) {
                // Something happened in setting up the request that triggered an Error
                errorMessage = err.message;
            }

            // Set the error message for display in the modal if it's a re-upload
            if (isReupload) {
                setUploadError(errorMessage);
            } else {
                message.error(errorMessage);
            }
        } finally {
            setUploadingExcel(false);
        }
    };

    // Function to get audio metadata using AudioContext API
    const getAudioMetadata = async (file: File): Promise<any> => {
        return new Promise((resolve) => {
            // First get basic duration using Audio element
            const audio = new Audio(URL.createObjectURL(file));
            audio.addEventListener('loadedmetadata', async () => {
                const duration = Math.floor(audio.duration * 1_000_000_000); // Convert to nanoseconds

                // Try to get more detailed metadata using AudioContext
                try {
                    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
                    const arrayBuffer = await file.arrayBuffer();
                    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

                    // Calculate approximate bitrate
                    const bitRate = Math.round((file.size * 8) / duration);

                    resolve({
                        duration,
                        channels: audioBuffer.numberOfChannels,
                        sampleRate: audioBuffer.sampleRate,
                        bitRate
                    });

                    // Close the audio context to free resources
                    if (audioContext.state !== 'closed') {
                        audioContext.close();
                    }
                } catch (error) {
                    console.error('Error extracting detailed audio metadata:', error);
                    // Return just the duration if detailed extraction fails
                    resolve({duration});
                }
            });

            audio.addEventListener('error', () => {
                console.error('Error loading audio for metadata extraction');
                resolve({duration: 0});
            });
        });
    };

    // Function to get image metadata
    const getImageMetadata = async (file: File): Promise<any> => {
        return new Promise((resolve) => {
            const img = new window.Image();
            img.onload = () => {
                resolve({
                    width: img.width,
                    height: img.height
                });
                // Clean up by revoking the object URL
                URL.revokeObjectURL(img.src);
            };
            img.onerror = () => {
                console.error('Error loading image for metadata extraction');
                resolve({});
                URL.revokeObjectURL(img.src);
            };
            img.src = URL.createObjectURL(file);
        });
    };

    // Function to determine file type
    const getFileType = (file: File): 'image' | 'audio' | 'other' => {
        if (file.type.startsWith('image/')) {
            return 'image';
        } else if (file.type.startsWith('audio/')) {
            return 'audio';
        } else {
            return 'other';
        }
    };


    // Function to handle audio and cover art file upload
    const handleFileUpload = async (files: File[]) => {
        if (files.length === 0) {
            message.error('Please select files to upload');
            return;
        }

        setUploadingFiles(true);
        let uploadProgress = 0;
        const totalFiles = files.length;
        let completedFiles = 0;

        try {
            // Process filenames to strip consecutive spaces
            const processedFiles = files.map(file => stripConsecutiveSpacesInFilename(file));
            console.log({processedFiles});

            // Group files by release based on filename
            const filesByRelease: Record<string, File[]> = {};

            // Iterate through batch items to get filenames for each release
            for (const item of batchItems) {
                console.log('Processing batch item:', item);
                const release = item.release;
                if (!release) continue;

                // Add cover art filename
                if (release.album_cover_filename) {
                    console.log('Looking for cover art file:', release.album_cover_filename);
                    // First try to find an exact match
                    let coverArtFile = processedFiles.find(file => file.name === release.album_cover_filename);

                    // If no exact match, try to find a match after stripping consecutive spaces from the expected filename
                    if (!coverArtFile) {
                        const processedExpectedName = release.album_cover_filename.replace(/ {2,}/g, ' ');
                        coverArtFile = processedFiles.find(file => file.name === processedExpectedName);
                    }

                    if (coverArtFile) {
                        console.log('Found cover art file for release', release.id);
                        if (!filesByRelease[release.id]) {
                            filesByRelease[release.id] = [];
                        }
                        filesByRelease[release.id].push(coverArtFile);
                    }
                }

                // Add track filenames
                for (const track of release.art_tracks || []) {
                    if (track.filename) {
                        console.log('Looking for track file:', track.filename);
                        // First try to find an exact match
                        let trackFile = processedFiles.find(file => file.name === track.filename);

                        // If no exact match, try to find a match after stripping consecutive spaces from the expected filename
                        if (!trackFile) {
                            const processedExpectedName = track.filename.replace(/ {2,}/g, ' ');
                            trackFile = processedFiles.find(file => file.name === processedExpectedName);
                        }

                        if (trackFile) {
                            console.log('Found track file for release', release.id);
                            if (!filesByRelease[release.id]) {
                                filesByRelease[release.id] = [];
                            }
                            filesByRelease[release.id].push(trackFile);
                        }
                    }
                }
            }

            // Upload files for each release
            for (const releaseId in filesByRelease) {
                console.log('Processing files for release:', releaseId);
                const releaseFiles = filesByRelease[releaseId];

                for (const file of releaseFiles) {
                    console.log('Uploading file:', file);

                    // Determine file type
                    const fileType = getFileType(file);

                    // Extract metadata based on file type
                    let audioMetadata = null;
                    let imageMetadata = null;

                    if (fileType === 'audio') {
                        audioMetadata = await getAudioMetadata(file);
                    } else if (fileType === 'image') {
                        imageMetadata = await getImageMetadata(file);
                    }

                    // Get presigned URL for upload
                    const presignedUrlResponse = await axiosApiInstance.post(
                        '/api/products/releases/generate-presigned-url-upload',
                        {
                            fileName: file.name,
                            fileType: file.type,
                            folderName: releaseId
                        }
                    );

                    const {presignedUrl} = presignedUrlResponse.data;
                    console.log('Got presigned URL:', presignedUrl);

                    // Upload file to S3
                    await axios.put(presignedUrl, file, {
                        headers: {
                            'Content-Type': file.type
                        },
                        onUploadProgress: (progressEvent) => {
                            const fileProgress = (progressEvent.loaded * 100) / progressEvent.total;
                            const fileContribution = fileProgress / totalFiles;
                            const baseProgress = (completedFiles * 100) / totalFiles;
                            const newProgress = Math.min(baseProgress + fileContribution, 100);
                            setUploadProgress(newProgress);
                        }
                    });
                    console.log('Successfully uploaded file:', file);

                    // After successful upload, update file metadata
                    try {
                        const metadataPayload: any = {
                            fileName: file.name,
                            folderName: releaseId
                        };

                        // Add audio metadata if available
                        if (audioMetadata) {
                            metadataPayload.duration = audioMetadata.duration;
                            metadataPayload.channels = audioMetadata.channels;
                            metadataPayload.sampleRate = audioMetadata.sampleRate;
                            metadataPayload.bitRate = audioMetadata.bitRate;
                        }

                        // Add image metadata if available
                        if (imageMetadata) {
                            metadataPayload.width = imageMetadata.width;
                            metadataPayload.height = imageMetadata.height;
                        }

                        await axiosApiInstance.post('/api/products/releases/update-file-metadata', metadataPayload);
                        console.log('Successfully updated metadata for file:', file.name);

                        // If this is an audio file, update the track's placement, duration, and audio_file_release_id
                        if (fileType === 'audio') {
                            // Find the matching track for this audio file
                            const release = batchItems.find(item => item.release_id === releaseId)?.release;
                            if (release) {
                                const matchingTrack = release.art_tracks?.find(track => track.filename === file.name);
                                if (matchingTrack) {
                                    console.log('Found matching track for file:', file.name, matchingTrack);

                                    // Update track placement using the same endpoint as in regular mode
                                    try {
                                        await axiosApiInstance.put('/api/products/art_tracks/placement', {
                                            track_id: matchingTrack.id,
                                            release_id: releaseId,
                                            track_isrc: matchingTrack.isrc,
                                            volume_number: matchingTrack.volume_number || 1,
                                            track_number: matchingTrack.track_number,
                                            duration: audioMetadata.duration
                                        });
                                        console.log('Successfully updated track placement for:', file.name);
                                    } catch (placementError) {
                                        console.error('Error updating track placement:', placementError);
                                        message.warning(`Track file uploaded but placement could not be updated for ${file.name}`);
                                    }
                                }
                            }
                        }
                    } catch (error) {
                        console.error('Error updating file metadata:', error);
                        // Continue even if metadata update fails
                    }

                    completedFiles++;
                }
            }

            message.success('Files uploaded successfully');

            // Fetch all release files to update the UI with the latest file status
            await fetchAllReleaseFiles(batchItems);

            // Fetch preview data
            fetchBatchPreviews();
        } catch (err) {
            console.error('Error uploading files:', err);
            message.error('Failed to upload files');
        } finally {
            setUploadingFiles(false);
        }
    };

    // Function to fetch batch previews
    const fetchBatchPreviews = async () => {
        try {
            setProcessingBatch(true);
            const response = await axiosApiInstance.get(`/api/products/batch_releases/${id}/previews`);
            if (response.status === 200) {
                // Store the preview data
                console.log('Preview data:', response.data.preview_data);
                setPreviewData(response.data.preview_data || {});

                // Only collect validation warnings if we're in the ExcelUploadStep
                // This ensures validation is done in ExcelUploadStep, not VerificationStep
                if (currentStep === 'upload-excel') {
                    const allWarnings: API.MusicDistribution.ValidationError[] = [];
                    for (const releaseId in response.data.preview_data) {
                        const releasePreview = response.data.preview_data[releaseId];
                        if (releasePreview.release?.validation_warnings) {
                            allWarnings.push(...releasePreview.release.validation_warnings);
                        }

                        // Also collect track-level warnings
                        if (releasePreview.release?.art_tracks) {
                            for (const track of releasePreview.release.art_tracks) {
                                if (track.validation_warnings) {
                                    allWarnings.push(...track.validation_warnings);
                                }
                            }
                        }
                    }
                    setWarnings(allWarnings);
                }
            } else {
                throw new Error('Failed to fetch batch previews');
            }
        } catch (err) {
            console.error('Error fetching batch previews:', err);
            message.error('Failed to fetch batch previews');
        } finally {
            setProcessingBatch(false);
        }
    };

    // Function to handle batch submission
    const handleSubmit = async () => {
        try {
            // Count how many UPCs and ISRCs will be created
            let upcCount = 0;
            let isrcCount = 0;

            for (const releaseId in previewData) {
                const releasePreview = previewData[releaseId];
                if (releasePreview.preview_upc) {
                    upcCount++;
                }
                if (releasePreview.preview_isrcs) {
                    isrcCount += Object.keys(releasePreview.preview_isrcs).length;
                }
            }

            // Set confirmation modal data - no validation warnings as per requirements
            setConfirmationModal({
                visible: true,
                upcCount,
                isrcCount,
                previewUPC: '',
                previewISRCs: {},
                validationWarnings: []
            });

            return true;
        } catch (error) {
            console.error('Validation failed:', error);
            return false;
        }
    };

    // Function to handle the actual submission after confirmation
    const handleConfirmedSubmit = async () => {
        try {
            // Close the confirmation modal
            setConfirmationModal({...confirmationModal, visible: false});

            // Assign identifiers
            await axiosApiInstance.post(`/api/products/batch_releases/${id}/assign-identifiers`);

            message.success('Batch processed successfully');
            fetchBatchPreviews()
            // navigate('/music');
            return true;
        } catch (error) {
            console.error('Submission failed:', error);
            message.error('Failed to submit batch');
            return false;
        }
    };

    // Function to handle batch deletion
    const handleDeleteBatch = async () => {
        try {
            await axiosApiInstance.delete(`/api/products/batch_releases/${id}`);
            message.success('Batch deleted successfully');
            navigate('/music');
        } catch (error) {
            console.error('Failed to delete batch:', error);
            message.error('Failed to delete batch');
        }
    };

    // Function to handle file deletion
    const handleDeleteFile = async (releaseId: string, fileName: string) => {
        try {
            const success = await deleteReleaseFile(releaseId, fileName);
            if (success) {
                // Update the uploadedFiles state by removing the deleted file
                setUploadedFiles(prevFiles => {
                    const updatedFiles = {...prevFiles};
                    if (updatedFiles[releaseId]) {
                        updatedFiles[releaseId] = updatedFiles[releaseId].filter(file => file.name !== fileName);
                    }
                    return updatedFiles;
                });
            }
        } catch (error) {
            console.error('Error deleting file:', error);
            message.error(`Failed to delete file ${fileName}`);
        }
    };

    if (loading) {
        return <Spin tip="Loading..."/>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    // Map currentStep to step number for Steps component
    const getStepNumber = () => {
        switch (currentStep) {
            case 'upload-excel':
                return 0;
            case 'upload-files':
                return 1;
            case 'verify':
                return 2;
            default:
                return 0;
        }
    };

    return (
        <PageContextProvider
            value={{
                releasesActionRef,
                listRef,
                artists,
                setArtists
            }}
        >
            <div className="release-entry-container" style={{width: '100%', maxWidth: 1600, margin: '0 auto'}}>
                <div className="release-entry-header"
                     style={{display: 'flex', justifyContent: 'space-between', marginBottom: 20}}>
                    <h2>Batch Process Releases{batch ? `: ${batch.name}` : ''}</h2>
                    {/*<div>*/}
                    {/*    <Button*/}
                    {/*        key="back"*/}
                    {/*        onClick={() => navigate('/music')}*/}
                    {/*        style={{marginRight: 8}}*/}
                    {/*    >*/}
                    {/*        Cancel*/}
                    {/*    </Button>*/}
                    {/*    {currentStep === 'verify' && (*/}
                    {/*        <Button*/}
                    {/*            key="submit"*/}
                    {/*            type="primary"*/}
                    {/*            onClick={handleSubmit}*/}
                    {/*            loading={processingBatch}*/}
                    {/*        >*/}
                    {/*            Submit*/}
                    {/*        </Button>*/}
                    {/*    )}*/}
                    {/*</div>*/}
                </div>

                <div style={{marginBottom: 20}}>
                    <Steps
                        current={getStepNumber()}
                        onChange={(current) => {
                            // Map step number back to step key
                            const stepKeys = ['upload-excel', 'upload-files', 'verify'];
                            const newStep = stepKeys[current];
                            setCurrentStep(newStep as BatchUploadStep);


                        }}
                        type="navigation"
                        items={[
                            {
                                title: 'Upload Excel',
                            },
                            {
                                title: 'Upload Files',
                            },
                            {
                                title: 'Verify',
                            }
                        ]}
                    />
                </div>

                <div>
                    {currentStep === 'upload-excel' && (
                        <ExcelUploadStep
                            excelFile={excelFile}
                            setExcelFile={setExcelFile}
                            uploadingExcel={uploadingExcel}
                            handleExcelUpload={handleExcelUpload}
                            setReuploadModalVisible={setReuploadModalVisible}
                            refreshMetadata={() => id ? fetchBatchData(id) : Promise.reject(new Error('No batch ID'))}
                        />
                    )}
                    {currentStep === 'upload-files' && (
                        <FileUploadStep
                            selectedFiles={selectedFiles}
                            setSelectedFiles={setSelectedFiles}
                            uploadingFiles={uploadingFiles}
                            uploadProgress={uploadProgress}
                            setUploadProgress={setUploadProgress}
                            handleFileUpload={handleFileUpload}
                            handleDeleteFile={handleDeleteFile}
                            fetchAllReleaseFiles={fetchAllReleaseFiles}
                            fetchingExistingFiles={fetchingExistingFiles}
                        />
                    )}
                    {currentStep === 'verify' && (
                        <VerificationStep
                            handleSubmit={handleSubmit}
                            handleDeleteBatch={handleDeleteBatch}
                            fetchAllReleaseFiles={fetchAllReleaseFiles}
                            fetchBatchPreviews={fetchBatchPreviews}
                            fetchingExistingFiles={fetchingExistingFiles}
                            processingBatch={processingBatch}
                        />
                    )}
                </div>
            </div>

            {/* Modals */}
            <BatchCreationModal
                modalVisible={modalVisible}
                batchName={batchName}
                setBatchName={setBatchName}
                batchDescription={batchDescription}
                setBatchDescription={setBatchDescription}
                handleModalSubmit={handleModalSubmit}
                onCancel={() => navigate('/music')}
            />

            <ReuploadExcelModal
                reuploadModalVisible={reuploadModalVisible}
                setReuploadModalVisible={setReuploadModalVisible}
                excelFile={excelFile}
                setExcelFile={setExcelFile}
                uploadingExcel={uploadingExcel}
                handleExcelUpload={handleExcelUpload}
                uploadError={uploadError}
                setUploadError={setUploadError}
            />

            <ConfirmationModal
                handleConfirmedSubmit={handleConfirmedSubmit}
            />
        </PageContextProvider>
    );
};

export default BatchReleasePage;
